import React, {
  useRef,
  useState,
  useEffect,
  useLayoutEffect,
  useContext,
  useCallback,
  useMemo,
} from 'react';
import {
  View,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  Linking,
  Platform,
  BackHandler,
  useWindowDimensions,
  ScrollView,
  Image,
} from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { TextInput as PaperTextInput } from 'react-native-paper';

import AntDesign from '@react-native-vector-icons/ant-design/static';
import Feather from '@react-native-vector-icons/feather/static';
import Ionicons from '@react-native-vector-icons/ionicons/static';
import LinearGradient from 'react-native-linear-gradient';
import { useDispatch } from 'react-redux';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { setEmployeePassword } from '../../services/SecureCredentials';
import PhoneInput, {
  ICountry,
  IPhoneInputRef,
} from 'rn-international-phone-number';
import { KeyboardStickyView } from 'react-native-keyboard-controller';
import KeyboardAwareFormScroll from '../../components/Reusables/KeyboardAwareFormScroll';
import Typography from '../../components/Reusables/Typography';
import { Picker } from '@react-native-picker/picker';
import { axiosinstance } from '../../axiosClient';
import {
  updateRegisterForm,
  updateFormValidation,
  clearEmployeeAndBusinessInfo,
} from '../../redux/AuthSlice';
import { useAppSelector } from '../../redux/helper';
import {
  setCurrentLocation,
  setCurrentLocationId,
} from '../../redux/locationSlice';
import appAnalytics from '../../services/appAnalytics';
import { buildAppAnalyticsContext } from '../../services/appAnalyticsContext';
import {
  dumpTokens,
  s,
  fs,
  v,
  borderWidth,
  normalizeEmail,
} from '../../utils/libs';
import useAuth from '../../hooks/useAuth';
import { resetOwnerActivationState } from '../../redux/ownerActivationSlice';
import { clearOwnerSetupHandoffMarker } from '../../services/ownerSetupSessionHandoff';
import { isTablet } from 'react-native-device-info';
import { theme, GTFonts } from '../../theme';
import NetInfo from '@react-native-community/netinfo';
import ErrorContext from '../../Context/ErrorContext';
import { PREFERRED_COUNTRIES } from '../../utils/phone';
import { useDetectedCountry } from '../../hooks/useDetectedCountry';
import { usePickerItemColor } from '../../hooks/usePickerItemColor';
import { shouldUseWebLayout } from '../../utils/layoutUtils';
import { getDetectedIanaTimezone } from '../../utils/timezone';

import { isPasswordValid } from '../../utils/passwordValidation';
import PasswordStrengthIndicator from '../../components/Reusables/PasswordStrengthIndicator';

const USER_REGEX = /^[A-z][A-z0-9-_]{2,23}$/;
const EML_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[A-Z]{2,}$/i;
// const PHONE_REGEX = /^(\+?254|0)(7|1)\d{8}$/; // replaced by library validation

// Types
interface Package {
  _id: string;
  bp_package_name: string;
}

const RegisterScreen = ({ navigation }: { navigation: any }) => {
  const { pickerItemColor, pickerItemColorSecondary } = usePickerItemColor();
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const isLargeScreen = windowWidth >= 768;
  const useWebLikeLayout = shouldUseWebLayout(windowWidth, windowHeight);
  const registerHeroImage = useMemo(
    () =>
      shouldUseWebLayout(windowWidth, windowHeight)
        ? require('../../assets/login/image3.webp')
        : require('../../assets/login/image2.webp'),
    [windowWidth, windowHeight],
  );
  const dispatch = useDispatch();
  const registerState = useAppSelector(state => state.auth.register);
  const { ownerInfo, formValidation } = registerState;
  const phoneInputRef = React.useRef<IPhoneInputRef | null>(null);
  const { setAuth } = useAuth();
  const { handleError } = useContext(ErrorContext);
  //UI-related local states
  const [userFocus, setUserFocus] = useState(false);
  const [usernameHasSpace, setUsernameHasSpace] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [userTouched, setUserTouched] = useState(false);
  const isIOS = Platform.OS === 'ios';

  const [passwordBlurred, setPasswordBlurred] = useState(false);
  const [firstNameFocus, setFirstNameFocus] = useState(false);
  const [lastNameFocus, setLastNameFocus] = useState(false);
  const [phoneFocused, setPhoneFocused] = useState(false);

  // international phone input state (align with Login screen)
  const [phoneInputValue, setPhoneInputValue] = useState<string>('');
  const [phoneSelectedCountry, setPhoneSelectedCountry] = useState<
    ICountry | undefined
  >(undefined);
  const { countryCode, iCountry } = useDetectedCountry();

  const buildE164From = (
    input: string,
    countryOverride?: ICountry | undefined,
  ): string => {
    const digitsOnly = String(input || '').replace(/[^0-9]/g, '');
    const country = countryOverride || phoneInputRef.current?.getCountry?.();
    const root = (country as any)?.idd?.root as string | undefined;
    const suffixes = (((country as any)?.idd?.suffixes as string[]) ||
      []) as string[];
    let code = '';
    if (root) {
      code = `${root}${suffixes?.[0] ?? ''}`.replace(/\s+/g, '');
      if (!code.startsWith('+')) code = `+${code}`;
    }
    if (code && digitsOnly) return `${code}${digitsOnly}`;
    if (digitsOnly) return `+${digitsOnly}`;
    return '';
  };

  const [packages, setPackages] = useState<Package[]>([]);

  const [errMsg, setErrMsg] = useState('');

  // Fetch packages on component mount
  useLayoutEffect(() => {
    getPackages();
  }, []);

  // Initialize selected country from detected SIM/IP
  useEffect(() => {
    if (!phoneSelectedCountry && iCountry) {
      setPhoneSelectedCountry(iCountry);
    }
  }, [phoneSelectedCountry, iCountry]);

  // Update validation effects to use ownerInfo
  useEffect(() => {
    dispatch(
      updateFormValidation({ validName: USER_REGEX.test(ownerInfo.username) }),
    );
  }, [ownerInfo.username]);

  // Validate first name
  useEffect(() => {
    dispatch(
      updateFormValidation({ validFirstName: ownerInfo.firstName.length >= 3 }),
    );
  }, [ownerInfo.firstName]);

  // Validate last name
  useEffect(() => {
    dispatch(
      updateFormValidation({ validLastName: ownerInfo.lastName.length >= 3 }),
    );
  }, [ownerInfo.lastName]);

  // Validate email
  useEffect(() => {
    dispatch(
      updateFormValidation({ validEmail: EML_REGEX.test(ownerInfo.email) }),
    );
  }, [ownerInfo.email]);

  // Validate phone using library validation
  useEffect(() => {
    const isValid = !!phoneInputRef.current?.isValidPhoneNumber;
    dispatch(updateFormValidation({ validPhone: isValid }));
  }, [ownerInfo.phoneNumber]);

  // Validate password and confirm password
  useEffect(() => {
    dispatch(
      updateFormValidation({
        validPwd: isPasswordValid(
          ownerInfo.password,
          ownerInfo.username,
          ownerInfo.email,
        ),
        validMatch: ownerInfo.password === ownerInfo.confirmPassword,
      }),
    );
  }, [
    ownerInfo.password,
    ownerInfo.confirmPassword,
    ownerInfo.username,
    ownerInfo.email,
  ]);

  // Clear error message when form fields change
  useEffect(() => {
    setErrMsg('');
  }, [
    ownerInfo.username,
    ownerInfo.password,
    ownerInfo.confirmPassword,
    ownerInfo.email,
    ownerInfo.phoneNumber,
    ownerInfo.firstName,
    ownerInfo.lastName,
  ]);

  // Handle back button - navigate to Login if no previous screen
  const handleBackPress = useCallback(() => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      navigation.navigate('Login');
    }
    return true;
  }, [navigation]);

  useFocusEffect(
    useCallback(() => {
      const backHandler = BackHandler.addEventListener(
        'hardwareBackPress',
        handleBackPress,
      );
      return () => backHandler.remove();
    }, [handleBackPress]),
  );

  // Fetch available packages
  const getPackages = async () => {
    try {
      const response = await axiosinstance.get('bp_get_packages_information');
      if (response.data.status === 200) {
        setPackages(response.data.data);
        // Set default package if available (prefer basic)
        if (response.data.data.length > 0) {
          const basicPackage = response.data.data.find(
            (pkg: Package) => pkg?.bp_package_name.toLowerCase() === 'basic',
          );
          if (basicPackage) {
            dispatch(updateRegisterForm({ userPackage: basicPackage._id }));
          } else {
            // Fallback to first non-free, non-executive package
            const fallback = response.data.data.find(
              (pkg: Package) =>
                pkg?.bp_package_name.toLowerCase() !== 'free' &&
                pkg?.bp_package_name.toLowerCase() !== 'executive',
            );
            dispatch(
              updateRegisterForm({
                userPackage: fallback?._id || response.data.data[0]._id,
              }),
            );
          }
        }
      }
    } catch (error) {
      console.log('Error fetching packages:', error);
    }
  };

  // Update form handlers
  const handleFormChange = (field: string, value: any) => {
    dispatch(updateRegisterForm({ [field]: value }));

    // Update validation
    switch (field) {
      case 'username':
        dispatch(updateFormValidation({ validName: USER_REGEX.test(value) }));
        break;
      case 'firstName':
        dispatch(updateFormValidation({ validFirstName: value.length >= 3 }));
        break;
      case 'lastName':
        dispatch(updateFormValidation({ validLastName: value.length >= 3 }));
        break;
      case 'email':
        dispatch(updateFormValidation({ validEmail: EML_REGEX.test(value) }));
        break;
      case 'phoneNumber':
        dispatch(
          updateFormValidation({
            validPhone: !!phoneInputRef.current?.isValidPhoneNumber,
          }),
        );
        break;
      case 'password':
        dispatch(
          updateFormValidation({
            validPwd: isPasswordValid(
              value,
              ownerInfo.username,
              ownerInfo.email,
            ),
            validMatch: value === ownerInfo.confirmPassword,
          }),
        );
        break;
      case 'confirmPassword':
        dispatch(
          updateFormValidation({ validMatch: value === ownerInfo.password }),
        );
        break;
    }
  };

  const handleUsernameChange = (raw: string) => {
    const hasWhitespace = /\s/.test(raw);
    setUsernameHasSpace(hasWhitespace);
    // Enforce "one word" username by stripping all whitespace (spaces, tabs, newlines).
    const sanitized = raw.replace(/\s+/g, '');
    handleFormChange('username', sanitized);
  };

  // Update the terms and conditions handler
  const handleTermsAcceptance = () => {
    dispatch(
      updateRegisterForm({
        termsAccepted: !ownerInfo.termsAccepted,
      }),
    );
  };

  // Update the package selection handler
  const handlePackageChange = (itemValue: string) => {
    dispatch(
      updateRegisterForm({
        userPackage: itemValue,
      }),
    );
  };

  const preRegisterCleanUp = async () => {
    dumpTokens();
    setAuth({ accessToken: '', refreshToken: '' });
    dispatch(clearEmployeeAndBusinessInfo());
    // A brand-new registration can never inherit a previous owner's setup
    // wizard/tracker progress or a stale session handoff marker.
    dispatch(resetOwnerActivationState());
    await clearOwnerSetupHandoffMarker();
  };

  // Handle form submission
  const handleSubmit = async () => {
    const ctx = buildAppAnalyticsContext();
    appAnalytics.track('acq_register_submit_attempted', { ...ctx });

    // Connectivity guard
    const netState = await NetInfo.fetch();
    const isOnline = !!(
      netState.isConnected &&
      (netState.isInternetReachable ?? false)
    );
    if (!isOnline) {
      appAnalytics.track('acq_register_failed', {
        ...ctx,
        error_category: 'network',
      });
      setErrMsg(
        'No internet connection. Please check your data or wifi and try again.',
      );
      handleError?.(
        'No internet connection. Please check your data or wifi and try again.',
        'error',
        true,
      );
      return;
    }
    preRegisterCleanUp();
    if (
      !formValidation.validName ||
      !formValidation.validPwd ||
      !formValidation.validEmail ||
      !formValidation.validFirstName ||
      !formValidation.validLastName ||
      !formValidation.validPhone ||
      !formValidation.validMatch ||
      !ownerInfo.termsAccepted ||
      !ownerInfo.userPackage
    ) {
      appAnalytics.track('acq_register_failed', {
        ...ctx,
        error_category: 'validation',
      });
      setErrMsg('Please check your form for errors');
      return;
    }

    setIsLoading(true);

    try {
      const response = await axiosinstance.post(
        '/bp_create_owner_account_information',
        {
          bp_username: ownerInfo.username,
          bp_first_name: ownerInfo.firstName,
          bp_last_name: ownerInfo.lastName,
          bp_email_address: normalizeEmail(ownerInfo.email),
          bp_user_identification_number: '',
          bp_user_profile_picture: '',
          bp_accepted_terms_and_condition: ownerInfo.termsAccepted,
          bp_agent_referral_code: '',
          bp_package_type: ownerInfo.userPackage,
          bp_phone_number: ownerInfo.phoneNumber,
          bp_password: ownerInfo.password,
          bp_free_trial_status: true,
          bp_billing_frequency: 'MONTHLY',
          bp_user_timezone: getDetectedIanaTimezone(),
        },
      );

      console.log('create_owner_account_information', response.data);

      if (response?.data?.status === 200) {
        dispatch(setCurrentLocation(response.data?.location));
        dispatch(setCurrentLocationId(response.data?.location._id));
        await setEmployeePassword(ownerInfo.password);
        await AsyncStorage.setItem('userId', response.data.data._id);

        appAnalytics.track('acq_register_succeeded', { ...ctx });

        // Navigate to verification screen
        navigation.navigate('Verification', {
          phone: ownerInfo.phoneNumber,
          email: normalizeEmail(ownerInfo.email),
        });
      } else {
        const msg = String(response.data.message || '').toLowerCase();
        appAnalytics.track('acq_register_failed', {
          ...ctx,
          error_category: msg.includes('exist')
            ? 'duplicate'
            : msg.includes('invalid')
              ? 'validation'
              : 'server',
        });
        setErrMsg(response.data.message || 'Registration failed');
      }
    } catch (error: any) {
      console.log('Registration error:', error);
      const msg = String(
        error?.response?.data?.message || error?.message || '',
      ).toLowerCase();
      appAnalytics.track('acq_register_failed', {
        ...ctx,
        error_category: !error?.response
          ? 'network'
          : msg.includes('exist')
            ? 'duplicate'
            : msg.includes('invalid')
              ? 'validation'
              : 'server',
      });
      setErrMsg(
        error?.response?.data?.message ||
          error?.message ||
          'Network error occurred',
      );
    } finally {
      setIsLoading(false);
    }
  };

  const renderValidationIcon = (isValid: boolean, isDirty: boolean = true) => {
    if (!isDirty) return null;

    return isValid ? (
      <AntDesign
        name="check"
        size={20}
        color="green"
        style={styles.validationIcon}
      />
    ) : (
      <Ionicons
        name="close-circle"
        size={20}
        color="red"
        style={styles.validationIcon}
      />
    );
  };

  const openTermsAndConditions = () => {
    Linking.canOpenURL(
      'https://archive.org/details/bizplus-point-of-sale-terms-of-service',
    )
      .then(supported => {
        if (supported) {
          Linking.openURL(
            'https://archive.org/details/bizplus-point-of-sale-terms-of-service',
          );
        }
      })
      .catch(err => console.error('Error opening URL:', err));
  };

  const openPricingPage = () => {
    Linking.canOpenURL('https://www.bizpluspos.co.ke/pricing')
      .then(supported => {
        if (supported) {
          Linking.openURL('https://www.bizpluspos.co.ke/pricing');
        }
      })
      .catch(err => console.error('Error opening URL:', err));
  };

  const renderError = (message: string, isVisible: any) => {
    if (!isVisible) return null;

    return <Typography style={styles.errorText}>{message}</Typography>;
  };

  const isFormValid =
    formValidation.validName &&
    formValidation.validPwd &&
    formValidation.validEmail &&
    formValidation.validFirstName &&
    formValidation.validLastName &&
    formValidation.validPhone &&
    formValidation.validMatch &&
    ownerInfo.termsAccepted &&
    ownerInfo.userPackage;

  // Web layout with centered card
  // Viewport-locked shell: left hero stays at viewport height; only the form
  // column scrolls when validation errors add content (Stripe/Linear pattern).
  if (useWebLikeLayout && isLargeScreen) {
    return (
      <View
        style={[
          styles.webContainer,
          { height: windowHeight, maxHeight: windowHeight },
        ]}>
        {/* Left Panel - Branding (viewport-pinned, never grows with form) */}
        <View style={styles.webLeftPanel}>
          <View style={styles.webBrandingContent}>
            <Image
              style={styles.webHeroImage}
              source={registerHeroImage}
              resizeMode="cover"
            />
            <LinearGradient
              colors={[
                'rgba(15,23,42,0.50)',
                'rgba(15,23,42,0.24)',
                'rgba(15,23,42,0.48)',
              ]}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={styles.webOverlay}>
              <Typography style={styles.webBrandTitle}>BizPlus POS</Typography>
              <Typography style={styles.webBrandSubtitle}>
                Join thousands of businesses simplifying their operations with
                BizPlus POS solution
              </Typography>
              <View style={styles.webFeatureList}>
                <View style={styles.webFeatureItem}>
                  <AntDesign name="check-circle" size={16} color={theme.colors.successLight} />
                  <Typography style={styles.webFeatureText}>
                    Smart Inventory Management
                  </Typography>
                </View>
                <View style={styles.webFeatureItem}>
                  <AntDesign name="check-circle" size={16} color={theme.colors.successLight} />
                  <Typography style={styles.webFeatureText}>
                    CashFlow Management
                  </Typography>
                </View>
                <View style={styles.webFeatureItem}>
                  <AntDesign name="check-circle" size={16} color={theme.colors.successLight} />
                  <Typography style={styles.webFeatureText}>
                    M-Pesa Integration
                  </Typography>
                </View>
              </View>
            </LinearGradient>
          </View>
        </View>

        {/* Right Panel - Form (independent scroll; errors never resize the hero) */}
        <View style={styles.webRightPanel}>
          <ScrollView
            style={styles.webScrollView}
            contentContainerStyle={styles.webScrollContent}
            showsVerticalScrollIndicator={false}>
            <View style={styles.webFormCard}>
              <View style={styles.webFormHeader}>
                <Typography style={styles.webBrandMark}>BizPlus POS</Typography>
                <Typography style={styles.webTitle}>
                  Create your account
                </Typography>
                <Typography style={styles.webSubtitle}>
                  Get started with BizPlus POS in a few minutes
                </Typography>
              </View>

              {errMsg ? (
                <View style={styles.webErrorContainer}>
                  <Typography style={styles.webErrorText}>{errMsg}</Typography>
                </View>
              ) : null}

              {/* User Info Section */}
              <Typography style={styles.webSectionLabel}>
                User Information
              </Typography>
              <View style={styles.webInputRow}>
                <View style={styles.webInputHalf}>
                  <PaperTextInput
                    mode="outlined"
                    label="First Name"
                    placeholder="John"
                    value={ownerInfo.firstName}
                    onChangeText={(text: string) =>
                      handleFormChange('firstName', text)
                    }
                    onFocus={() => setFirstNameFocus(true)}
                    onBlur={() => setFirstNameFocus(false)}
                    outlineColor={theme.colors.borderGray200}
                    activeOutlineColor={theme.colors.primary}
                    outlineStyle={styles.webInputOutline}
                    style={styles.webInput}
                  />
                </View>
                <View style={styles.webInputHalf}>
                  <PaperTextInput
                    mode="outlined"
                    label="Last Name"
                    placeholder="Doe"
                    value={ownerInfo.lastName}
                    onChangeText={(text: string) =>
                      handleFormChange('lastName', text)
                    }
                    onFocus={() => setLastNameFocus(true)}
                    onBlur={() => setLastNameFocus(false)}
                    outlineColor={theme.colors.borderGray200}
                    activeOutlineColor={theme.colors.primary}
                    outlineStyle={styles.webInputOutline}
                    style={styles.webInput}
                  />
                </View>
              </View>

              <PaperTextInput
                mode="outlined"
                label="Username"
                placeholder="johndoe"
                value={ownerInfo.username}
                onChangeText={handleUsernameChange}
                onFocus={() => setUserFocus(true)}
                onBlur={() => {
                  setUserFocus(false);
                  setUserTouched(true);
                }}
                autoCapitalize="none"
                autoCorrect={false}
                spellCheck={false}
                outlineColor={theme.colors.borderGray200}
                activeOutlineColor={theme.colors.primary}
                outlineStyle={styles.webInputOutline}
                style={styles.webInput}
              />
              {renderError(
                'Username cannot contain spaces',
                userFocus && usernameHasSpace,
              )}
              {renderError(
                'Must be 3-24 characters and start with a letter',
                userFocus && ownerInfo.username && !formValidation.validName,
              )}

              {/* Contact Info Section */}
              <Typography style={[styles.webSectionLabel, { marginTop: 12 }]}>
                Contact Information
              </Typography>
              <PhoneInput
                phoneInputStyles={{
                  container: {
                    height: 52,
                    backgroundColor: theme.colors.surface,
                    borderWidth: 1,
                    borderColor: theme.colors.borderGray200,
                    borderRadius: 12,
                    width: '100%',
                    marginBottom: 12,
                  },
                  input: {
                    fontFamily: GTFonts.semibold,
                    fontSize: 16,
                    lineHeight: 20,
                    color: theme.colors.textGray700,
                    outlineStyle: 'none',
                  } as any,
                  callingCode: {
                    fontFamily: GTFonts.semibold,
                    fontSize: 16,
                    lineHeight: 20,
                    color: theme.colors.textGray700,
                  },
                }}
                ref={phoneInputRef}
                defaultCountry={countryCode as any}
                value={phoneInputValue}
                modalType="bottomSheet"
                popularCountries={PREFERRED_COUNTRIES}
                placeholder="720 000 000"
                onFocus={() => setPhoneFocused(true)}
                onBlur={() => setPhoneFocused(false)}
                onChangePhoneNumber={val => {
                  const startsWithZero = val.startsWith('0');
                  if (startsWithZero) {
                    val = val.slice(1);
                  }
                  setPhoneInputValue(val);
                  const full = buildE164From(val, phoneSelectedCountry);
                  handleFormChange('phoneNumber', full);
                }}
                country={phoneSelectedCountry}
                onChangeCountry={c => {
                  setPhoneSelectedCountry(c);
                  const full = buildE164From(phoneInputValue, c);
                  handleFormChange('phoneNumber', full);
                }}
              />

              <PaperTextInput
                mode="outlined"
                label="Email"
                placeholder="johndoe@gmail.com"
                value={ownerInfo.email}
                onChangeText={(text: string) => handleFormChange('email', text)}
                keyboardType="email-address"
                autoCapitalize="none"
                outlineColor={theme.colors.borderGray200}
                activeOutlineColor={theme.colors.primary}
                outlineStyle={styles.webInputOutline}
                style={styles.webInput}
              />

              {/* Package Selection */}
              <Typography style={[styles.webSectionLabel, { marginTop: 12 }]}>
                Choose Package
              </Typography>
              <View style={styles.webPickerContainer}>
                <Picker
                  selectedValue={ownerInfo.userPackage}
                  onValueChange={handlePackageChange}
                  style={styles.webPicker}>
                  <Picker.Item
                    label="Select a package"
                    value=""
                    color={pickerItemColorSecondary}
                  />
                  {packages
                    .filter(
                      pkg =>
                        pkg?.bp_package_name.toLowerCase() !== 'executive' &&
                        pkg?.bp_package_name.toLowerCase() !== 'free',
                    )
                    .map(pkg => (
                      <Picker.Item
                        key={pkg._id}
                        label={
                          pkg?.bp_package_name.toLowerCase() === 'premium'
                            ? `${pkg?.bp_package_name} (Recommended)`
                            : pkg?.bp_package_name
                        }
                        value={pkg._id}
                        color={pickerItemColor}
                      />
                    ))}
                </Picker>
              </View>

              {/* Password Section */}
              <Typography style={[styles.webSectionLabel, { marginTop: 12 }]}>
                Security
              </Typography>
              <PaperTextInput
                mode="outlined"
                label="Password"
                placeholder="Create a strong password"
                value={ownerInfo.password}
                onChangeText={(text: string) =>
                  handleFormChange('password', text)
                }
                onBlur={() => {
                  if (ownerInfo.password.length > 0) setPasswordBlurred(true);
                }}
                secureTextEntry={!showPassword}
                outlineColor={theme.colors.borderGray200}
                activeOutlineColor={theme.colors.primary}
                outlineStyle={styles.webInputOutline}
                style={styles.webInput}
                right={
                  <PaperTextInput.Icon
                    icon={showPassword ? 'eye-off' : 'eye'}
                    onPress={() => setShowPassword(!showPassword)}
                  />
                }
              />
              <PasswordStrengthIndicator
                password={ownerInfo.password}
                username={ownerInfo.username}
                email={ownerInfo.email}
                hasBlurred={passwordBlurred}
              />

              <PaperTextInput
                mode="outlined"
                label="Confirm Password"
                placeholder="Confirm your password"
                value={ownerInfo.confirmPassword}
                onChangeText={(text: string) =>
                  handleFormChange('confirmPassword', text)
                }
                // Web: treat Enter as clicking "Create Account"
                onKeyPress={(e: any) => {
                  const key = e?.nativeEvent?.key ?? e?.key;
                  if (key !== 'Enter') return;
                  e?.preventDefault?.();
                  e?.stopPropagation?.();
                  if (isLoading || !isFormValid) return;
                  void handleSubmit();
                }}
                // Native: press "done"/"enter" to submit
                returnKeyType="done"
                onSubmitEditing={() => {
                  if (isLoading || !isFormValid) return;
                  void handleSubmit();
                }}
                secureTextEntry={true}
                outlineColor={theme.colors.borderGray200}
                activeOutlineColor={theme.colors.primary}
                outlineStyle={styles.webInputOutline}
                style={styles.webInput}
              />
              {renderError(
                'Passwords must match',
                ownerInfo.confirmPassword && !formValidation.validMatch,
              )}

              {/* Terms */}
              <TouchableOpacity
                onPress={handleTermsAcceptance}
                style={styles.webCheckboxContainer}>
                <Feather
                  name={ownerInfo.termsAccepted ? 'check-square' : 'square'}
                  size={20}
                  color={
                    ownerInfo.termsAccepted ? theme.colors.primary : theme.colors.textGray500
                  }
                />
                <Typography style={styles.webCheckboxLabel}>
                  I agree to the{' '}
                  <Typography
                    style={styles.webLink}
                    onPress={openTermsAndConditions}>
                    Terms and Conditions
                  </Typography>
                </Typography>
              </TouchableOpacity>

              {/* Submit Button */}
              <TouchableOpacity
                style={[
                  styles.webButton,
                  (!isFormValid || isLoading) && styles.webButtonDisabled,
                ]}
                onPress={handleSubmit}
                disabled={!isFormValid || isLoading}>
                {isLoading ? (
                  <ActivityIndicator color={theme.colors.textInverse} />
                ) : (
                  <Typography style={styles.webButtonText}>
                    Create Account
                  </Typography>
                )}
              </TouchableOpacity>

              {/* Login Link */}
              <View style={styles.webLoginLink}>
                <Typography style={styles.webLoginText}>
                  Already have an account?{' '}
                </Typography>
                <TouchableOpacity onPress={() => navigation.popTo('Login')}>
                  <Typography style={styles.webLoginLinkText}>
                    Log In
                  </Typography>
                </TouchableOpacity>
              </View>
            </View>
          </ScrollView>
        </View>
      </View>
    );
  }

  // Mobile layout (original)
  return (
    <View style={styles.container}>
      <KeyboardAwareFormScroll
        contentContainerStyle={styles.scrollContainer}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
        // baseBottomPadding={v(20)}
        bottomOffset={v(240)}>
        <View style={styles.headerContainer}>
          <View style={styles.infoBanner}>
            <Typography style={styles.infoText}>
              BizPlus POS is Simple, Powerful and Reliable.
              <Typography
                style={styles.linkwhite}
                onPress={openPricingPage}></Typography>
            </Typography>
          </View>
        </View>

        {errMsg ? (
          <View style={styles.errorContainer}>
            <Typography style={styles.errorMessage}>{errMsg}</Typography>
          </View>
        ) : null}

        <View style={styles.formContainer}>
          <Typography style={styles.sectionTitle}>
            Create Your BizPlus Account
          </Typography>
          {/* Username Input */}
          <View style={styles.inputGroup}>
            <View style={styles.labelContainer}>
              <Typography style={styles.label}>User Information</Typography>
            </View>
            <PaperTextInput
              mode="outlined"
              label="Username"
              placeholder="doe"
              value={ownerInfo.username}
              onChangeText={handleUsernameChange}
              onFocus={() => setUserFocus(true)}
              onBlur={() => {
                setUserFocus(false);
                setUserTouched(true);
              }}
              autoCapitalize="none"
              outlineColor={theme.colors.borderGray}
              activeOutlineColor={theme.colors.primary}
              error={
                !!(
                  (userFocus && usernameHasSpace) ||
                  (userFocus && ownerInfo.username && !formValidation.validName)
                )
              }
              style={styles.input}
            />
            {renderError(
              'Username cannot contain spaces',
              userFocus && usernameHasSpace,
            )}
            {renderError(
              'Must be 3-24 characters and start with a letter',
              userFocus && ownerInfo.username && !formValidation.validName,
            )}
          </View>

          {/* First Name Input */}
          <View style={styles.inputGroup}>
            <PaperTextInput
              mode="outlined"
              label="First Name"
              placeholder="John"
              value={ownerInfo.firstName}
              onChangeText={(text: string) =>
                handleFormChange('firstName', text)
              }
              onFocus={() => setFirstNameFocus(true)}
              onBlur={() => setFirstNameFocus(false)}
              outlineColor={theme.colors.borderGray}
              activeOutlineColor={theme.colors.primary}
              error={
                !!(
                  firstNameFocus &&
                  ownerInfo.firstName &&
                  !formValidation.validFirstName
                )
              }
              style={styles.input}
            />
            {renderError(
              'Must have at least 3 characters',
              firstNameFocus &&
                ownerInfo.firstName &&
                !formValidation.validFirstName,
            )}
          </View>

          {/* Last Name Input */}
          <View style={styles.inputGroup}>
            <PaperTextInput
              mode="outlined"
              label="Last Name"
              placeholder="Doe"
              value={ownerInfo.lastName}
              onChangeText={(text: string) =>
                handleFormChange('lastName', text)
              }
              onFocus={() => setLastNameFocus(true)}
              onBlur={() => setLastNameFocus(false)}
              outlineColor={theme.colors.borderGray}
              activeOutlineColor={theme.colors.primary}
              error={
                !!(
                  lastNameFocus &&
                  ownerInfo.lastName &&
                  !formValidation.validLastName
                )
              }
              style={styles.input}
            />
            {renderError(
              'Must have at least 3 characters',
              lastNameFocus &&
                ownerInfo.lastName &&
                !formValidation.validLastName,
            )}
          </View>
          {/* Phone Number Input (international) */}
          <View style={styles.inputGroup}>
            <View style={styles.labelContainer}>
              <Typography style={styles.label}>Contact Information</Typography>
            </View>
            <PhoneInput
              phoneInputStyles={{
                container: {
                  height: v(50),
                  backgroundColor: theme.colors.surface,
                  borderWidth: borderWidth(Platform.OS === 'web' ? 0.5 : 1),
                  width: '100%',
                  marginTop: v(5),
                  marginBottom: v(10),
                },
                input: {
                  fontFamily: GTFonts.semibold,
                  fontSize: fs(16),
                  lineHeight: fs(18),
                  ...(Platform.OS === 'web' && { outlineStyle: 'none' }),
                } as any,
                callingCode: {
                  fontFamily: GTFonts.semibold,
                  fontSize: fs(16),
                  lineHeight: fs(18),
                },
              }}
              ref={phoneInputRef}
              defaultCountry={countryCode as any}
              value={phoneInputValue}
              modalType="bottomSheet"
              popularCountries={PREFERRED_COUNTRIES}
              modalPopularCountriesTitle="Popular countries"
              modalAllCountriesTitle="All countries"
              placeholder="720 000 000"
              minBottomsheetHeight={'50%'}
              initialBottomsheetHeight={'80%'}
              maxBottomsheetHeight={'92%'}
              onFocus={() => {
                setPhoneFocused(true);
              }}
              onBlur={() => {
                setPhoneFocused(false);
              }}
              onChangePhoneNumber={val => {
                const startsWithZero = val.startsWith('0');
                if (startsWithZero) {
                  val = val.slice(1);
                }
                setPhoneInputValue(val);
                const full = buildE164From(val, phoneSelectedCountry);
                handleFormChange('phoneNumber', full);
              }}
              country={phoneSelectedCountry}
              onChangeCountry={c => {
                setPhoneSelectedCountry(c);
                const full = buildE164From(phoneInputValue, c);
                handleFormChange('phoneNumber', full);
              }}
            />
            {renderError(
              'Please enter a valid phone number',
              phoneFocused &&
                ownerInfo.phoneNumber &&
                !formValidation.validPhone,
            )}
          </View>

          {/* Email Input */}
          <View style={styles.inputGroup}>
            <PaperTextInput
              mode="outlined"
              label="Email"
              placeholder="johndoe@gmail.com"
              value={ownerInfo.email}
              onChangeText={(text: string) => handleFormChange('email', text)}
              onFocus={() => setUserFocus(true)}
              onBlur={() => setUserFocus(false)}
              keyboardType="email-address"
              autoCapitalize="none"
              outlineColor={theme.colors.borderGray}
              activeOutlineColor={theme.colors.primary}
              error={
                !!(userFocus && ownerInfo.email && !formValidation.validEmail)
              }
              style={styles.input}
            />
            {renderError(
              'Please enI wter a valid email address',
              userFocus && ownerInfo.email && !formValidation.validEmail,
            )}
          </View>

          {/* Package Selection (hidden on iOS, default is set to "free" in getPackages) */}
          {!isIOS && (
            <View style={styles.inputGroup}>
              <View style={styles.labelContainer} hitSlop={10}>
                <Typography style={styles.label}>
                  Choose your Package{': '}
                </Typography>
              </View>
              <View style={styles.pickerContainer}>
                <Picker
                  selectedValue={ownerInfo.userPackage}
                  onValueChange={handlePackageChange}
                  dropdownIconColor={theme.colors.textSecondary}
                  style={styles.picker}>
                  <Picker.Item
                    label="Select a package"
                    value=""
                    color={pickerItemColorSecondary}
                  />
                  {packages
                    .filter(
                      pkg =>
                        pkg?.bp_package_name.toLowerCase() !== 'executive' &&
                        pkg?.bp_package_name.toLowerCase() !== 'free',
                    )
                    .map(pkg => (
                      <Picker.Item
                        key={pkg._id}
                        style={styles.pickerItem}
                        label={
                          pkg?.bp_package_name.toLowerCase() === 'premium'
                            ? `${pkg?.bp_package_name} (Recommended)`
                            : pkg?.bp_package_name
                        }
                        value={pkg._id}
                        color={pickerItemColor}
                      />
                    ))}
                </Picker>
              </View>
            </View>
          )}

          {/* Password Input */}
          <View style={styles.inputGroup}>
            <View style={styles.labelContainer}>
              <Typography style={styles.label}>Password</Typography>
            </View>
            <PaperTextInput
              mode="outlined"
              label="Password"
              placeholder="Password"
              value={ownerInfo.password}
              onChangeText={(text: string) =>
                handleFormChange('password', text)
              }
              onFocus={() => setUserFocus(true)}
              onBlur={() => {
                setUserFocus(false);
                if (ownerInfo.password.length > 0) setPasswordBlurred(true);
              }}
              secureTextEntry={!showPassword}
              outlineColor={theme.colors.borderGray}
              activeOutlineColor={theme.colors.primary}
              error={
                !!(
                  passwordBlurred &&
                  ownerInfo.password &&
                  !formValidation.validPwd
                )
              }
              style={styles.input}
              right={
                <PaperTextInput.Icon
                  icon={showPassword ? 'eye-off' : 'eye'}
                  onPress={() => setShowPassword(!showPassword)}
                />
              }
            />
            <PasswordStrengthIndicator
              password={ownerInfo.password}
              username={ownerInfo.username}
              email={ownerInfo.email}
              hasBlurred={passwordBlurred}
            />
          </View>

          {/* Confirm Password Input */}
          <View style={styles.inputGroup}>
            <PaperTextInput
              mode="outlined"
              label="Confirm Password"
              placeholder="Confirm Password"
              value={ownerInfo.confirmPassword}
              onChangeText={(text: string) =>
                handleFormChange('confirmPassword', text)
              }
              // Web: treat Enter as clicking "Sign Up"
              onKeyPress={(e: any) => {
                const key = e?.nativeEvent?.key ?? e?.key;
                if (key !== 'Enter') return;
                e?.preventDefault?.();
                e?.stopPropagation?.();
                if (isLoading || !isFormValid) return;
                void handleSubmit();
              }}
              // Native: press "done"/"enter" to submit
              returnKeyType="done"
              onSubmitEditing={() => {
                if (isLoading || !isFormValid) return;
                void handleSubmit();
              }}
              onFocus={() => setUserFocus(true)}
              onBlur={() => setUserFocus(false)}
              secureTextEntry={true}
              outlineColor={theme.colors.borderGray}
              activeOutlineColor={theme.colors.primary}
              error={
                !!(
                  userFocus &&
                  ownerInfo.confirmPassword &&
                  !formValidation.validMatch
                )
              }
              style={styles.input}
            />
            {renderError(
              'Passwords must match',
              userFocus &&
                ownerInfo.confirmPassword &&
                !formValidation.validMatch,
            )}
          </View>

          {/* Terms and Conditions */}
          <TouchableOpacity
            hitSlop={10}
            onPress={handleTermsAcceptance}
            style={styles.checkboxContainer}>
            {ownerInfo.termsAccepted ? (
              <Feather
                name="check-square"
                size={24}
                style={styles.checkbox}
                color={theme.colors.primaryLight}
              />
            ) : (
              <Feather
                name="square"
                size={24}
                style={styles.checkbox}
                color={theme.colors.primaryLight}
              />
            )}
            <View style={styles.checkboxLabelRow}>
              <Typography style={styles.checkboxLabel}>I agree to </Typography>
              <Typography
                style={[styles.checkboxLabel, styles.link]}
                onPress={openTermsAndConditions}>
                Terms and conditions
              </Typography>
            </View>
          </TouchableOpacity>

          {/* Login Link */}
          <View style={styles.loginLinkContainer}>
            <Typography style={styles.loginText}>
              Already have an account?{' '}
            </Typography>
            <TouchableOpacity
              hitSlop={10}
              onPress={() => navigation.popTo('Login')}>
              <Typography style={styles.loginLink}>Log in</Typography>
            </TouchableOpacity>
          </View>
        </View>
      </KeyboardAwareFormScroll>

      <KeyboardStickyView offset={{ closed: 0, opened: v(30) }}>
        <View style={styles.stickyButtonContainer}>
          <TouchableOpacity
            style={[
              styles.button,
              (!isFormValid || isLoading) && styles.disabledButton,
            ]}
            onPress={handleSubmit}
            disabled={!isFormValid || isLoading}>
            {isLoading ? (
              <ActivityIndicator color={theme.colors.textInverse} />
            ) : (
              <Typography style={styles.buttonText}>Create Account</Typography>
            )}
          </TouchableOpacity>
        </View>
      </KeyboardStickyView>
    </View>
  );
};

const styles = StyleSheet.create({
  // ============ WEB STYLES ============
  webContainer: {
    flexDirection: 'row',
    backgroundColor: theme.colors.textGray900,
    width: '100%',
    overflow: 'hidden',
  },
  webLeftPanel: {
    flex: 3,
    height: '100%',
    position: 'relative',
    overflow: 'hidden',
  } as any,
  webBrandingContent: {
    ...StyleSheet.absoluteFillObject,
  },
  webHeroImage: {
    ...StyleSheet.absoluteFillObject,
    width: '100%',
    height: '100%',
  },
  webOverlay: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: 'center',
    alignItems: 'center',
    padding: s(48),
  },
  webBrandTitle: {
    fontSize: fs(42),
    lineHeight: fs(52),
    fontWeight: '700',
    color: theme.colors.primaryDark,
    fontFamily: GTFonts.semibold,
    marginBottom: v(16),
    textAlign: 'center',
    textShadowColor: 'rgba(0,0,0,0.35)',
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 8,
  },
  webBrandSubtitle: {
    fontSize: fs(16),
    color: theme.colors.textInverse,
    fontFamily: GTFonts.semibold,
    textAlign: 'center',
    maxWidth: s(360),
    lineHeight: fs(24),
    marginBottom: v(32),
    textShadowColor: 'rgba(0,0,0,0.55)',
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 6,
  },
  webFeatureList: {
    gap: v(12),
  },
  webFeatureItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: s(10),
  },
  webFeatureText: {
    fontSize: fs(14),
    lineHeight: fs(20),
    color: theme.colors.textInverse,
    fontFamily: GTFonts.semibold,
    textShadowColor: 'rgba(0,0,0,0.45)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 4,
  },
  webRightPanel: {
    flex: 2,
    height: '100%',
    backgroundColor: theme.colors.surface,
    overflow: 'hidden',
    paddingVertical: v(40),
    paddingHorizontal: s(48),
  },
  webScrollView: {
    flex: 1,
    width: '100%',
    backgroundColor: theme.colors.surface,
  },
  webScrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: v(24),
  },
  webFormCard: {
    width: '100%',
    maxWidth: s(440),
    backgroundColor: 'transparent',
  },
  webFormHeader: {
    marginBottom: v(28),
  },
  webBrandMark: {
    fontSize: fs(15),
    lineHeight: fs(20),
    color: theme.colors.primary,
    fontFamily: GTFonts.semibold,
    marginBottom: v(20),
  },
  webWelcomeText: {
    fontSize: fs(13),
    lineHeight: fs(18),
    color: theme.colors.primary,
    fontFamily: GTFonts.semibold,
    marginBottom: v(6),
  },
  webTitle: {
    fontSize: fs(28),
    lineHeight: fs(34),
    color: theme.colors.textGray900,
    fontFamily: GTFonts.semibold,
    marginBottom: v(8),
  },
  webSubtitle: {
    fontSize: fs(15),
    lineHeight: fs(22),
    color: theme.colors.textGray500,
    fontFamily: GTFonts.regular,
  },
  webErrorContainer: {
    backgroundColor: theme.colors.errorLight,
    borderRadius: s(8),
    padding: s(12),
    marginBottom: v(16),
    borderLeftWidth: borderWidth(3),
    borderLeftColor: theme.colors.error,
  },
  webErrorText: {
    color: theme.colors.errorDark,
    fontSize: fs(13),
    lineHeight: fs(18),
    fontFamily: GTFonts.regular,
  },
  webSectionLabel: {
    fontSize: fs(12),
    lineHeight: fs(16),
    fontWeight: '600',
    color: theme.colors.textGray500,
    fontFamily: GTFonts.semibold,
    marginTop: v(8),
    marginBottom: v(12),
    ...(Platform.OS === 'web'
      ? { textTransform: 'uppercase' as const, letterSpacing: 0.6 }
      : { textTransform: 'none' as const, letterSpacing: 0 }),
  },
  webInputRow: {
    flexDirection: 'row',
    gap: s(12),
    marginBottom: v(4),
  },
  webInputHalf: {
    flex: 1,
  },
  webInput: {
    backgroundColor: theme.colors.surface,
    marginBottom: v(12),
    fontSize: fs(14),
    lineHeight: fs(20),
  },
  webInputOutline: {
    borderRadius: s(8),
  },
  webPickerContainer: {
    borderWidth: borderWidth(1),
    borderColor: theme.colors.borderGray200,
    borderRadius: s(8),
    backgroundColor: theme.colors.surface,
    marginBottom: v(12),
    overflow: 'hidden',
  },
  webPicker: {
    height: v(52),
    fontSize: fs(14),
    lineHeight: fs(20),
    ...(Platform.OS === 'web' && {
      borderWidth: 0,
      backgroundColor: 'transparent',
      outlineStyle: 'none',
      outlineWidth: 0,
      outlineColor: 'transparent',
      paddingHorizontal: 12,
    }),
  } as any,
  webCheckboxContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: s(10),
    marginTop: v(16),
    marginBottom: v(20),
  },
  webCheckboxLabel: {
    fontSize: fs(13),
    justifyContent: 'center',
    alignSelf: 'center',
    lineHeight: fs(18),
    color: theme.colors.textSecondary,
    // fontFamily: GTFonts.regular,
  },
  webLink: {
    fontSize: fs(13),
    lineHeight: fs(18),
    color: theme.colors.primary,
    fontWeight: '600',
    textDecorationLine: 'underline',
  },
  webButton: {
    // Match native mobile Register enabled button
    backgroundColor: theme.colors.primary,
    borderRadius: s(10),
    height: v(50),
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
  } as any,
  webButtonDisabled: {
    // Match native mobile Register disabled button
    backgroundColor: theme.colors.primaryLight,
    cursor: 'not-allowed',
  } as any,
  webButtonText: {
    fontSize: fs(15),
    lineHeight: fs(20),
    fontWeight: '600',
    color: theme.colors.textInverse,
    fontFamily: GTFonts.semibold,
  },
  webLoginLink: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: v(8),
    paddingTop: v(20),
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: theme.colors.borderGray200,
  },
  webLoginText: {
    fontSize: fs(14),
    lineHeight: fs(20),
    color: theme.colors.textGray500,
    fontFamily: GTFonts.regular,
  },
  webLoginLinkText: {
    fontSize: fs(14),
    lineHeight: fs(20),
    color: theme.colors.primary,
    fontWeight: '600',
    fontFamily: GTFonts.semibold,
  },

  // ============ MOBILE STYLES ============
  container: {
    flex: 1,
    backgroundColor: theme.colors.surface,
  },
  scrollContainer: {
    flexGrow: 1,
    paddingHorizontal: s(8),
    gap: v(8),
  },
  headerContainer: {
    alignItems: 'center',
  },

  infoBanner: {
    backgroundColor: theme.colors.warningDark,
    borderRadius: s(8),
    paddingVertical: s(12),
    paddingHorizontal: s(7),
    marginVertical: v(10),
    // marginBottom: v(16),
    width: '100%',
  },
  infoText: {
    color: theme.colors.textInverse,
    textAlign: 'center',
    fontSize: fs(15),
    lineHeight: fs(20),
    fontFamily: GTFonts.semibold,
  },
  errorContainer: {
    backgroundColor: theme.colors.errorLight,
    borderRadius: s(8),
    marginHorizontal: s(8),
    marginBottom: v(16),
    padding: s(12),
  },
  errorMessage: {
    color: theme.colors.errorDark,
    fontSize: fs(14),
    lineHeight: fs(18),
    textAlign: 'center',
  },
  smallError: {
    color: theme.colors.errorDark,
    fontSize: fs(12),
    lineHeight: fs(16),
    marginTop: v(4),
  },
  formContainer: {},
  inputGroup: {
    marginBottom: v(4),
  },
  labelContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: v(6),
  },
  label: {
    fontSize: fs(17),
    color: theme.colors.textGray900,
    lineHeight: fs(20),
    fontFamily: GTFonts.semibold,
    marginVertical: v(5),
    marginRight: s(8),
  },
  validationIcon: {
    marginLeft: s(4),
  },
  input: {
    backgroundColor: theme.colors.surface,
    height: v(50),
    fontSize: fs(16),
    lineHeight: fs(20),
    marginBottom: v(10),
    color: theme.colors.textGray900,
  },
  focusedInput: {
    color: theme.colors.shadow,
    // borderWidth: 2,
    // borderColor: theme.colors.primaryDark,
  },
  errorText: {
    color: theme.colors.errorDark,
    fontSize: fs(12),
    lineHeight: fs(16),
    marginTop: v(4),
  },
  phoneContainer: {
    width: '100%',
    height: v(50),
    paddingLeft: s(12),
    borderWidth: borderWidth(Platform.OS === 'web' ? 0.5 : 1),
    borderColor: theme.colors.borderGray,
    borderRadius: s(8),
    backgroundColor: theme.colors.surface,
  },
  phonePlaceholder: {
    fontSize: fs(18),
    lineHeight: fs(22),
  },
  phoneTextContainer: {
    borderLeftWidth: s(Platform.OS === 'web' ? 0.5 : 1),
    borderLeftColor: theme.colors.borderGray200,
    paddingVertical: s(0),
  },
  phoneCodeText: {
    fontSize: fs(18),
    lineHeight: fs(22),
  },
  phoneTextInput: {
    fontSize: fs(18),
    lineHeight: fs(22),
  },
  pickerContainer: {
    borderWidth: borderWidth(Platform.OS === 'web' ? 0.5 : 1),
    borderColor: theme.colors.borderGray,
    height: v(52),
    borderRadius: s(8),
    marginBottom: v(10),
    overflow: 'hidden',
    backgroundColor: theme.colors.surface,
  },
  picker: {
    fontSize: fs(16),
    lineHeight: fs(20),
    color: theme.colors.shadow,
    ...(Platform.OS === 'web' && {
      borderWidth: 0,
      backgroundColor: 'transparent',
      outlineStyle: 'none',
      outlineWidth: 0,
      outlineColor: 'transparent',
      paddingHorizontal: 12,
    }),
  } as any,
  pickerItem: {
    fontSize: fs(16),
    lineHeight: fs(20),
  },
  pickerRow: {
    alignItems: 'center',
    borderWidth: borderWidth(1.6),
    borderColor: theme.colors.primaryLight,
    borderRadius: s(8),
    height: v(50),
  },
  pickerText: {
    color: theme.colors.shadow,
    padding: s(8),
    fontSize: fs(16),
    lineHeight: fs(20),
  },
  passwordContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: borderWidth(Platform.OS === 'web' ? 0.5 : 1),
    borderColor: theme.colors.borderGray,
    borderRadius: s(8),
    height: v(50),
    backgroundColor: theme.colors.surface,
  },
  passwordInput: {
    flex: 1,
    color: theme.colors.textGray900,
    padding: s(8),
    fontSize: fs(16),
    lineHeight: fs(20),
  },
  passwordVisibilityButton: {
    padding: s(8),
  },
  checkboxContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: v(16),
  },
  checkbox: {
    marginRight: s(8),
    alignSelf: 'center',
    justifyContent: 'center',
    alignItems: 'center',
  },
  checkboxLabel: {
    fontSize: fs(14),
    lineHeight: fs(18),
    color: theme.colors.text,
  },
  checkboxLabelRow: {
    flexDirection: 'row',
    flexWrap: 'nowrap',
    alignItems: 'center',
  },
  linkwhite: {
    color: theme.colors.textInverse,
    fontWeight: 'bold',
    textDecorationLine: 'underline',
  },
  link: {
    color: theme.colors.primary,
    fontSize: fs(13),
    lineHeight: fs(18),
    fontWeight: 'bold',
    textDecorationLine: 'underline',
  },
  button: {
    backgroundColor: theme.colors.primary,
    paddingVertical: s(14),
    borderRadius: s(12),
    alignItems: 'center',
  },
  buttonText: {
    color: 'white',
    fontFamily: GTFonts.regular,
    fontWeight: '700',
    fontSize: fs(16),
    lineHeight: fs(20),
  },
  disabledButton: {
    backgroundColor: theme.colors.primaryLight,
  },
  loginLinkContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: v(20),
  },
  loginText: {
    fontSize: fs(16),
    lineHeight: fs(20),
    color: theme.colors.textSecondary,
  },
  loginLink: {
    fontSize: fs(isTablet() ? 18 : 16),
    lineHeight: fs(isTablet() ? 22 : 20),
    color: theme.colors.primary,
    fontWeight: '600',
    textDecorationLine: 'underline',
  },
  sectionTitle: {
    fontFamily: GTFonts.semibold,
    fontSize: fs(20),
    lineHeight: fs(24),
    textAlign: 'center',
    color: theme.colors.text,
    marginBottom: v(10),
  },
  stickyButtonContainer: {
    backgroundColor: theme.colors.surface,
    paddingHorizontal: s(8),
    paddingVertical: s(20),
    borderTopWidth: s(Platform.OS === 'web' ? 0.5 : 1),
    borderTopColor: theme.colors.borderGray200,
  },
});

export default RegisterScreen;
